'use client';

import { apiFetch } from '@/lib/utils/api';
import { Button, Row, Text } from '@slims/ui/atoms';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
	CardBadge,
	CardGrid,
	ConfirmationDialog,
	DataTable,
	type DataTableColumn,
	KpiBig,
	KpiSkeleton,
	ListSkeleton,
	PanelCard,
	PanelCardSkeleton,
	StatusBlock,
	Tag,
} from '@slims/ui/molecules';
import { PageHeader } from '@slims/ui/templates';
import Link from '@/lib/next/link';
import { useCallback, useEffect, useState } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';

interface Org {
	id: string;
	parentId: string | null;
	nome: string;
	soci: number;
	membersCount: number;
	podsCount: number;
	kwp: number;
	disabled?: boolean;
}

interface AdminUser {
	id: string;
	email: string;
	fullName: string | null;
	role: string;
	/** Per-CER memberships (org + role). */
	orgs?: { id: string; nome: string; role: string }[];
}

export default function SuperAdminDashboard() {
	const [orgs, setOrgs] = useState<Org[]>([]);
	const [orgsLoading, setOrgsLoading] = useState(true);
	const [orgsError, setOrgsError] = useState(false);
	const [users, setUsers] = useState<AdminUser[]>([]);
	const [usersLoading, setUsersLoading] = useState(true);
	const [usersError, setUsersError] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<Org | null>(null);
	const [deleting, setDeleting] = useState(false);

	const loadOrgs = useCallback(() => {
		setOrgsError(false);
		apiFetch('/api/admin/orgs')
			.then((r) => r.json())
			.then((data: unknown) => {
				// Un errore lato API arriva come corpo JSON valido (`{ error: ... }`),
				// non come eccezione: senza questo controllo CER/Configurazioni/POD/kWp
				// si azzeravano tutti insieme in silenzio, senza dire perché.
				if (!Array.isArray(data)) {
					setOrgsError(true);
					return;
				}
				setOrgs(data as Org[]);
			})
			.catch(() => setOrgsError(true))
			.finally(() => setOrgsLoading(false));
	}, []);

	const loadUsers = useCallback(() => {
		setUsersError(false);
		apiFetch('/api/admin/users')
			.then((r) => r.json())
			.then((data: unknown) => {
				if (!Array.isArray(data)) {
					setUsersError(true);
					return;
				}
				setUsers(data as AdminUser[]);
			})
			.catch(() => setUsersError(true))
			.finally(() => setUsersLoading(false));
	}, []);

	useEffect(() => {
		loadOrgs();
		loadUsers();
	}, [loadOrgs, loadUsers]);

	// Toggling "disabilitata", creating/deleting a CER or config, or editing a
	// membership from this page or another tab must reach this table live —
	// counts (soci/configurazioni) and disabled badges included.
	useEffect(() => {
		const unsubOrgs = subscribeToTable(
			'organizations-superadmin-list',
			{ event: '*', schema: 'public', table: 'organizations' },
			() => loadOrgs(),
		);
		const unsubMemberships = subscribeToTable(
			'user-org-memberships-superadmin-list',
			{ event: '*', schema: 'public', table: 'user_org_memberships' },
			() => loadUsers(),
		);
		return () => {
			unsubOrgs();
			unsubMemberships();
		};
	}, [loadOrgs, loadUsers]);

	// Un solo flag per tutto il pannello: i 5 KPI e la tabella CER leggono
	// entrambi da orgs+users, quindi si sbloccano insieme invece che a scatti
	// (stesso principio di Dashboard/Monitoraggio/Incentivi).
	const loading = orgsLoading || usersLoading;
	// Stesso principio per l'errore: un fallimento su uno dei due fetch basta a
	// rendere i KPI e la tabella inattendibili (0 CER non vuol dire "nessuna
	// CER" se il conteggio non si è nemmeno caricato).
	const loadError = orgsError || usersError;

	const userCount = users.length;
	// Top-level orgs are CERs; orgs with a parentId are their configurations.
	const cers = orgs.filter((o) => !o.parentId);
	const configs = orgs.filter((o) => o.parentId);
	// Numero di configurazioni per CER — la CER è il contenitore, le configurazioni
	// sono i suoi figli (INB-105: niente più colonna "tipo").
	const configCountByCer = configs.reduce<Record<string, number>>((acc, o) => {
		if (o.parentId) acc[o.parentId] = (acc[o.parentId] ?? 0) + 1;
		return acc;
	}, {});
	// Admins grouped by each CER where they hold an admin membership.
	const adminsByOrg = users.reduce<Record<string, AdminUser[]>>((acc, u) => {
		for (const o of u.orgs ?? []) {
			if (o.role !== 'admin') continue;
			(acc[o.id] ??= []).push(u);
		}
		return acc;
	}, {});
	const totalKwp = cers.reduce((acc, o) => acc + (o.kwp ?? 0), 0);
	const totalPods = orgs.reduce((acc, o) => acc + (o.podsCount ?? 0), 0);

	const handleDeleteConfirmed = async () => {
		if (!confirmDelete) return;
		setDeleting(true);
		try {
			const res = await apiFetch(`/api/admin/orgs/${confirmDelete.id}`, { method: 'DELETE' });
			if (!res.ok) {
				const body = (await res.json().catch(() => ({}))) as { error?: string };
				throw new Error(body.error ?? 'Eliminazione non riuscita');
			}
			setOrgs((prev) => prev.filter((o) => o.id !== confirmDelete.id));
			toast.success(`CER «${confirmDelete.nome}» eliminata`);
			setConfirmDelete(null);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Eliminazione non riuscita');
		} finally {
			setDeleting(false);
		}
	};

	const cerColumns: DataTableColumn<Org>[] = [
		{
			key: 'nome',
			header: 'Nome',
			render: (org) => (
				<>
					<strong>{org.nome}</strong>{' '}
					{org.disabled && <CardBadge tone="orange">Disabilitata</CardBadge>}
				</>
			),
		},
		{
			key: 'admins',
			header: 'Amministratori',
			render: (org) => {
				const orgAdmins = adminsByOrg[org.id] ?? [];
				if (orgAdmins.length === 0)
					return (
						<Text as="span" variant="micro" tone="muted">
							- nessuno -
						</Text>
					);
				return (
					<Row wrap gap="xxs">
						{orgAdmins.map((a) => (
							<Tag key={a.id} tone="purple">
								{a.fullName || a.email}
							</Tag>
						))}
					</Row>
				);
			},
		},
		{
			key: 'configurazioni',
			header: 'Configurazioni',
			variant: 'monoAccent',
			render: (org) => configCountByCer[org.id] ?? 0,
		},
		{ key: 'soci', header: 'Soci', variant: 'monoAccent', render: (org) => org.membersCount },
		// Come gli altri conteggi della riga: l'arancione non diceva nulla di
		// diverso (INB-374).
		{ key: 'kwp', header: 'kWp', variant: 'monoAccent', render: (org) => org.kwp },
		{
			key: 'azioni',
			header: 'Azioni',
			align: 'right',
			render: (org) => (
				<Row justify="end" gap="xs">
					<Button asChild variant="outline" size="sm">
						<Link href={`/cer/orgs/${org.id}/details`}>Dettagli</Link>
					</Button>
					<Button
						type="button"
						variant="ghostDanger"
						size="icon"
						title="Elimina"
						onClick={() => setConfirmDelete(org)}
					>
						<Trash2 />
					</Button>
				</Row>
			),
		},
	];

	return (
		<div>
			{/* Il conteggio CER e il badge SA erano ridondanti: la card "CER" qui sotto
			    e il ruolo nell'header dicono già la stessa cosa (INB-105). */}
			<PageHeader
				size="display"
				title="Gestione CER"
				description="Crea le comunità energetiche e assegna gli amministratori."
			/>

			{/* KPI — CER, Configurazioni, Utenti, POD, kWp (INB-105) */}
			{loading ? (
				<CardGrid columns={5}>
					<PanelCardSkeleton title="CER" badge>
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="Configurazioni" badge>
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="Utenti" badge>
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="POD" badge>
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="kWp" badge>
						<KpiSkeleton />
					</PanelCardSkeleton>
				</CardGrid>
			) : loadError ? (
				// Un fetch fallito arriva come corpo JSON valido (`{ error }`), non come
				// eccezione: senza questo banner i 5 KPI si azzeravano insieme in
				// silenzio, indistinguibili da "non esiste ancora nessuna CER".
				<StatusBlock kind="error" size="lg" message="Impossibile caricare CER, configurazioni e conteggi.">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => {
							loadOrgs();
							loadUsers();
						}}
					>
						Riprova
					</Button>
				</StatusBlock>
			) : (
				<CardGrid columns={5}>
					<PanelCard title="CER" aside={<CardBadge tone="green">attive</CardBadge>}>
						<KpiBig value={cers.length} tone="accent" unit="comunità energetiche" />
					</PanelCard>

					{/* Niente badge «totali»: accanto a un titolo lungo andava a capo sul
				    laptop, e contraddiceva «configurazioni attive» sotto (INB-345). */}
				<PanelCard title="Configurazioni">
						<KpiBig
							value={configs.length}
							tone="purple"
							unit="configurazioni attive"
							trend={`su ${cers.length} CER`}
						/>
					</PanelCard>

					<PanelCard title="Utenti" aside={<CardBadge tone="blue">globali</CardBadge>}>
						<KpiBig value={userCount} tone="success" unit="utenti registrati" />
					</PanelCard>

					<PanelCard title="POD" aside={<CardBadge tone="blue">censiti</CardBadge>}>
						<KpiBig value={totalPods} tone="accent" unit="POD totali piattaforma" />
					</PanelCard>

					<PanelCard title="kWp" aside={<CardBadge tone="orange">installati</CardBadge>}>
						<KpiBig value={Math.round(totalKwp)} tone="warn" unit="kWp totali piattaforma" />
					</PanelCard>
				</CardGrid>
			)}

			{/* CER list — nascosta insieme ai KPI sopra: senza dati reali "Nessuna
			    CER trovata" sarebbe la stessa bugia del conteggio a zero. */}
			{!loadError && (
				<PanelCard
					title="CER"
					aside={
						<Row gap="sm">
							{/* Il conteggio compare solo a dato pronto: a "0 totali" durante il
							    fetch si leggerebbe come nessuna CER, non come "sto contando". */}
							{!loading && <CardBadge tone="blue">{cers.length} totali</CardBadge>}
							<Button asChild variant="accent" size="sm">
								<Link href="/cer/orgs/new">+ Nuova</Link>
							</Button>
						</Row>
					}
				>
					{loading ? (
						<ListSkeleton rows={5} avatar={false} />
					) : cers.length === 0 ? (
						<Text tone="muted" align="center">
							Nessuna CER trovata
						</Text>
					) : (
						<DataTable columns={cerColumns} rows={cers} rowKey={(org) => org.id} />
					)}
				</PanelCard>
			)}

			{/* Conferma eliminazione */}
			<ConfirmationDialog
				open={!!confirmDelete}
				onOpenChange={(open) => {
					if (!open) setConfirmDelete(null);
				}}
				title="Elimina CER"
				description={`Sei sicuro di voler eliminare ${confirmDelete?.nome}? Questa operazione è irreversibile e rimuoverà tutti i dati associati.`}
				confirmText={deleting ? 'Eliminazione...' : 'Elimina'}
				cancelText="Annulla"
				onConfirm={handleDeleteConfirmed}
				isLoading={deleting}
				variant="destructive"
			/>
		</div>
	);
}
